import { AdvanceId, FeeId, PaymentDueId, PrincipalModificationId } from '../../BrandedIds';
import { Posting } from './Posting';

// Type-level constraint: "to move account X, the posting must carry
// attribution Y." Three parallel maps — symmetric, debit-only,
// credit-only — though only the symmetric map has active rules. See
// "Direction sensitivity" below for why the obvious direction-aware
// candidates (`cash`, `chargeoff`) don't fit cleanly.

export type AccountAttributionRequirement = {
  principalReceivable: { paymentDueId: PaymentDueId };
  interestReceivable: { paymentDueId: PaymentDueId };
  escrowReceivable: { paymentDueId: PaymentDueId };
  // Placeholder liability paired with `escrowReceivable`; traces to
  // the same statement.
  escrowObligation: { paymentDueId: PaymentDueId };
  delinquentInterestReceivable: { paymentDueId: PaymentDueId };
  feeReceivable: { feeId: FeeId };
  // Advances are stored as fees on the snapshot but use a distinct
  // `AdvanceId` brand on the ledger. See `asAdvanceId` in Charges.ts.
  advancePrincipal: { advanceId: AdvanceId };
  advanceInterest: { advanceId: AdvanceId };
  advanceEscrow: { advanceId: AdvanceId };
  advanceFunding: { advanceId: AdvanceId };
  modification: { modificationId: PrincipalModificationId };
};

export type DebitOnlyAttributionRequirement = {};

export type CreditOnlyAttributionRequirement = {};

type SymmetricAttributableAccount = keyof AccountAttributionRequirement;
type DebitOnlyAttributableAccount = keyof DebitOnlyAttributionRequirement;
type CreditOnlyAttributableAccount = keyof CreditOnlyAttributionRequirement;

type UnionToIntersection<U> = (U extends unknown ? (x: U) => void : never) extends (x: infer I) => void ? I : never;

// Identity for intersection — accounts absent from a requirement map
// contribute no constraint.
type Empty = Record<never, never>;

type SymmetricRequirement<A> = A extends SymmetricAttributableAccount ? AccountAttributionRequirement[A] : Empty;
type DebitRequirement<A> = A extends DebitOnlyAttributableAccount ? DebitOnlyAttributionRequirement[A] : Empty;
type CreditRequirement<A> = A extends CreditOnlyAttributableAccount ? CreditOnlyAttributionRequirement[A] : Empty;

export type RequiredAttribution<DR, CR> = UnionToIntersection<
  SymmetricRequirement<DR> | SymmetricRequirement<CR> | DebitRequirement<DR> | CreditRequirement<CR>
>;

// Attribution keys that `R` requires but `P` doesn't satisfy
// (missing, optional/nullable, or wrong type). `never` when satisfied.
type MissingAttribution<P, R> = {
  [K in keyof R]: K extends keyof P ? (R[K] extends P[K] ? (undefined extends P[K] ? K : never) : K) : K;
}[keyof R];

export type AttributionViolationsByKind = {
  [K in Posting['kind']]: MissingAttribution<
    Extract<Posting, { kind: K }>,
    RequiredAttribution<Extract<Posting, { kind: K }>['debitAccount'], Extract<Posting, { kind: K }>['creditAccount']>
  >;
};

export type ViolatingKinds = {
  [K in keyof AttributionViolationsByKind]: AttributionViolationsByKind[K] extends never ? never : K;
}[keyof AttributionViolationsByKind];

// Compile-time enforcement: any kind missing required attribution
// surfaces here, with details in AttributionViolationsByKind[<kind>].
const _enforceAttribution: never = {} as ViolatingKinds;
void _enforceAttribution;

// Direction-aware rules (debit-only / credit-only) are wired up but
// have no active rules. The obvious candidates — `cash`, `chargeoff` —
// don't fit because `reversalOf` swaps accounts while preserving
// attribution: a rule like "DR cash requires paymentId" would catch
// reversal kinds (RefundReversal, DrawReversal) that DR cash with a
// drawId / disbursementId instead. Modelling that needs OR-alternative
// or forward-only filtering, neither of which is implemented.

// Runtime mirror of the symmetric map for handler / fixture validation.
export const accountAttributionRequirementKeys = {
  principalReceivable: ['paymentDueId'],
  interestReceivable: ['paymentDueId'],
  escrowReceivable: ['paymentDueId'],
  escrowObligation: ['paymentDueId'],
  delinquentInterestReceivable: ['paymentDueId'],
  feeReceivable: ['feeId'],
  advancePrincipal: ['advanceId'],
  advanceInterest: ['advanceId'],
  advanceEscrow: ['advanceId'],
  advanceFunding: ['advanceId'],
  modification: ['modificationId'],
} as const satisfies {
  [K in SymmetricAttributableAccount]: readonly (keyof AccountAttributionRequirement[K])[];
};
